import React, { useMemo, useState, useContext, useEffect, useCallback } from 'react';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardTitle } from '../../../../components/bootstrap/Card';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import TicketsModal from './TicketsModal';
import BillingChargesDB from '../../../../database/wrappers/billingCharges';
import StoreDB from '../../../../database/wrappers/store';
import { where } from 'firebase/firestore';
import { DefaultContext } from '../../../../contexts/default';
import showNotification from '../../../../components/extras/showNotification';
import api from '../../../../services/api';
import Popovers from '../../../../components/bootstrap/Popovers';
import Spinner from '../../../../components/bootstrap/Spinner';

const TicketsSubscription = () => {
    const { estabSelected } = useContext(DefaultContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [approvingIds, setApprovingIds] = useState([]);

    useEffect(() => {
        if (!estabSelected) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const storeDB = new StoreDB(estabSelected);
                const stores = await storeDB.getAll();
                const storeMap = stores.reduce((acc, store) => {
                    acc[store.id] = store.name;
                    return acc;
                }, {});

                const db = new BillingChargesDB();
                const charges = await db.getAll(where('estabId', '==', estabSelected));

                const reports = charges.filter(charge => charge.report);

                const newRows = reports.map(charge => {
                    const justification = charge.report.justification || '';
                    const truncatedDesc = justification.length > 15
                        ? justification.substring(0, 15) + '...'
                        : justification;

                    return {
                        id: charge.id,
                        date: charge.report.updated_at?.toDate().toLocaleString('pt-BR') || '--',
                        store: storeMap[charge.storeId] || 'Loja desconhecida',
                        responsible: charge.report.userName || '--',
                        reason: charge.report.reason || 'Contestação',
                        description: truncatedDesc,
                        status: charge.report.status === 'OPEN' ? 'Pendente' : 'Concluído',
                        original: charge
                    };
                });

                setRows(newRows);
            } catch (error) {
                showNotification('Erro', 'Erro ao carregar dados', 'danger');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [estabSelected, refreshKey]);

    const handleApprove = useCallback(async (id) => {
        try {
            setApprovingIds(prev => [...prev, id]);
            await api.put('billing/reportStatus', {
                billingId: id,
                status: 'RESOLVED'
            });
            showNotification('Sucesso', 'Chamado aprovado com sucesso', 'success');
            setRefreshKey(old => old + 1);
        } catch (error) {
            console.error(error);
            showNotification('Erro', 'Erro ao aprovar chamado', 'danger');
        } finally {
            setApprovingIds(prev => prev.filter(itemId => itemId !== id));
        }
    }, []);

    const columns = useMemo(() => [
        { label: 'DATA', field: 'date' },
        { label: 'LOJA', field: 'store' },
        { label: 'RESPONSÁVEL', field: 'responsible' },
        {
            label: 'MOTIVO',
            field: 'reason',
            format: (row) => {
                const map = {
                    'cobranca_indevida': 'Cobrança indevida',
                    'servico_nao_prestado': 'Serviço não prestado',
                    'outro': 'Outro'
                };
                return map[row.reason] || row.reason;
            }
        },
        { label: 'DESCRIÇÃO', field: 'description' },
        {
            label: 'STATUS',
            field: 'status',
            format: row => {
                let color = 'success';
                let icon = 'Circle';

                if (row.status === 'Pendente') {
                    color = 'warning';
                    icon = 'AccessTime';
                }

                return (
                    <Button
                        isLight
                        color={color}
                        icon={icon}
                        className='text-nowrap'
                        size="sm"
                    >
                        {row.status}
                    </Button>
                )
            }
        },
        {
            label: 'AÇÃO',
            field: 'actions',
            format: (row) => (
                <div className='d-flex gap-2'>
                    <Popovers trigger='hover' desc="Ver descrição completa">
                        <Button
                            icon='Visibility'
                            color='dark'
                            size='sm'
                            shadow='sm'
                            onClick={() => {
                                setSelectedTicket(row.original);
                                setIsModalOpen(true);
                            }}
                        />
                    </Popovers>
                    {row.status === 'Pendente' && (
                        <Popovers trigger='hover' desc="Aprovar">
                            <Button
                                icon={approvingIds.includes(row.id) ? null : 'Check'}
                                color='success'
                                size='sm'
                                shadow='sm'
                                isDisable={approvingIds.includes(row.id)}
                                onClick={() => handleApprove(row.id)}
                            >
                                {approvingIds.includes(row.id) && (
                                    <Spinner isSmall inButton='onlyIcon' />
                                )}
                            </Button>
                        </Popovers>
                    )}
                </div>
            )
        }
    ], [handleApprove, approvingIds]);

    return (
        <PageWrapper title="Chamados">
            <SubHeader>
                <SubHeaderLeft>
                    <Breadcrumb
                        list={[
                            { title: 'Assinatura', to: '/subscription/tickets' },
                            { title: 'Chamados', to: '/subscription/tickets' },
                        ]}
                    />
                </SubHeaderLeft>
            </SubHeader>
            <Page container='fluid'>
                <Card>
                    <CardHeader className="mb-0 pb-0">
                        <CardTitle>
                            <h3>Chamados</h3>
                        </CardTitle>
                    </CardHeader>
                    <CardBody>
                        <div className="row d-flex align-items-end">
                            <SelectEstablishment class="col-md-6" />
                        </div>
                        <TableCustom
                            columns={columns}
                            rows={rows}
                            fileName={'Chamados'}
                        />
                    </CardBody>
                </Card>
            </Page>
            <TicketsModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} data={selectedTicket} />
        </PageWrapper>
    );
};

export default TicketsSubscription;
